import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { apiFetch } from '@/lib/http';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, LogIn } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { signInWithPassword, supabase } from '@/lib/authBootstrap';
import { useAuth } from '@/lib/auth-store';
import { useQueryClient } from '@tanstack/react-query';
import DebugInfo from '@/components/DebugInfo';
import { shouldBlockOperatorWebLogin } from '@/lib/webLoginAccess';

interface LoginPageProps {
  onLoginSuccess?: (user: any, requirePasswordChange?: boolean) => void;
}

interface PublicTheme {
  navBgColor: string;
  navTextColor: string;
  primaryColor: string;
  fontFamily: string;
  logoUrl: string | null;
}

const FONT_FAMILIES: Record<string, string> = {
  inter: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  roboto: "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  openSans: "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  lato: "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  montserrat: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  mulish: "'Mulish', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  poppins: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  sourceSans: "'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  nunito: "'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  raleway: "'Raleway', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  ubuntu: "'Ubuntu', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  system: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  mono: "'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', 'Droid Sans Mono', monospace",
};

const DEFAULT_THEME: PublicTheme = {
  navBgColor: '#0F3A3F',
  navTextColor: '#FFFFFF',
  primaryColor: '#3B82F6',
  fontFamily: 'inter',
  logoUrl: null,
};

export default function LoginPage({ onLoginSuccess }: LoginPageProps) {
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [theme, setTheme] = useState<PublicTheme>(DEFAULT_THEME);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { dispatch } = useAuth();

  useEffect(() => {
    fetch('/api/look-and-feel/public')
      .then((res) => res.ok ? res.json() : null)
      .then((data) => { if (data) setTheme(data); })
      .catch(() => {});
  }, []);

  // Pre-fill email from URL parameter (e.g., after password setup)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setEmail(emailParam);
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  const fontStack = FONT_FAMILIES[theme.fontFamily] || FONT_FAMILIES.inter;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      console.log('[AUTH_SUPABASE] Login attempt for:', email);
      await signInWithPassword(email, password);
      console.log('[AUTH_SUPABASE] Login successful, checking user role');

      // Check if user is an operator — operators should use the mobile app
      try {
        const meRes = await apiFetch('/api/users/me');
        if (!meRes.ok) throw new Error('Unable to verify your account. Please try again.');
        const meData = await meRes.json();
        if (!meData?.user?.role) throw new Error('Unable to verify your account. Please try again.');
        if (shouldBlockOperatorWebLogin(meData.user)) {
          console.log('[AUTH_SUPABASE] Operator login blocked on web app');
          await supabase.auth.signOut();
          setError('Operators should use the ReActor mobile app to log in. This web portal is for admin users only.');
          setIsLoading(false);
          return;
        }
      } catch (roleCheckError) {
        console.warn('[AUTH_SUPABASE] Role check failed:', roleCheckError);
        await supabase.auth.signOut();
        throw roleCheckError;
      }

      console.log('[AUTH_SUPABASE] Login successful, invalidating auth queries');
      try {
        await queryClient.invalidateQueries({ queryKey: ['auth'] });
        queryClient.removeQueries({ queryKey: ['auth', 'user'], exact: true });
        await queryClient.invalidateQueries({ queryKey: ['auth', 'user'] });
      } catch (invalidateError) {
        console.warn('[AUTH_SUPABASE] Failed to invalidate auth queries:', invalidateError);
      }
      try { dispatch({ type: 'RESET' }); } catch { }
      toast({ title: 'Login successful', description: 'Welcome back!' });
      if (onLoginSuccess) onLoginSuccess({ email }, false);
      window.location.href = '/';
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      console.error('[AUTH_SUPABASE] Login failed:', errorMessage);
      setError(errorMessage);
      toast({ title: 'Login failed', description: errorMessage, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: theme.navBgColor, fontFamily: fontStack }}
    >
      <DebugInfo />
      <Card
        className="w-full max-w-md border-0 shadow-2xl"
        style={{ backgroundColor: theme.navBgColor, color: theme.navTextColor }}
      >
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-6">
            {theme.logoUrl ? (
              <img
                src={theme.logoUrl}
                alt="Logo"
                className="h-10 max-w-[220px] object-contain"
              />
            ) : (
              <h1 className="text-2xl font-bold" style={{ color: theme.navTextColor }}>
                ReActor
              </h1>
            )}
          </div>
          <CardTitle
            className="text-2xl text-center font-semibold"
            style={{ color: theme.navTextColor, fontFamily: fontStack }}
          >
            Sign in to your account
          </CardTitle>
          <CardDescription
            className="text-center"
            style={{ color: `${theme.navTextColor}99`, fontFamily: fontStack }}
          >
            Enter your username to access the platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate data-form-type="other">
            <div className="space-y-2">
              <Label
                htmlFor="email"
                style={{ color: theme.navTextColor, fontFamily: fontStack }}
              >
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck="false"
                data-1p-ignore
                data-lpignore="true"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white/15"
                style={{ color: theme.navTextColor }}
              />
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="password"
                style={{ color: theme.navTextColor, fontFamily: fontStack }}
              >
                Password
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white/15"
                style={{ color: theme.navTextColor }}
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full font-semibold"
              disabled={isLoading}
              style={{
                backgroundColor: theme.primaryColor,
                color: '#ffffff',
                fontFamily: fontStack,
              }}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign in
                </>
              )}
            </Button>

            <div className="text-center">
              <Button
                variant="link"
                className="text-sm"
                style={{ color: `${theme.navTextColor}CC`, fontFamily: fontStack }}
                onClick={() => setLocation('/forgot-password')}
              >
                Forgot your password?
              </Button>
            </div>

            <div className="text-center pt-2">
              <span
                className="text-xs"
                style={{ color: `${theme.navTextColor}55`, fontFamily: fontStack }}
              >
                v{__APP_VERSION__}
              </span>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
